import { cn } from "@/lib/utils"
import type { KeyboardEvent, ReactNode } from "react"

interface EntityListRowProps {
  isSelected: boolean
  hasFlag?: boolean
  onClick: () => void
  children: ReactNode
  className?: string
  rowId?: string
  checkbox?: ReactNode
}

/**
 * Shared list row shell used by all entity list panels.
 * Provides consistent hover, selected, and flag indicator styles.
 */
export function EntityListRow({
  isSelected,
  hasFlag,
  onClick,
  children,
  className,
  rowId,
  checkbox,
}: EntityListRowProps) {
  const rowClassName = cn(
    "group flex w-full cursor-pointer items-start gap-3 border-b border-grey4 px-4 py-3 text-left transition-colors",
    isSelected ? "bg-primary/10" : "bg-transparent hover:bg-grey3/60",
    className,
  )

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onClick()
    }
  }

  if (checkbox) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        data-row-id={rowId}
        className={rowClassName}
      >
        <span
          className="mt-0.5 shrink-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {checkbox}
        </span>
        {hasFlag && (
          <span className="h-1.5 w-1.5 shrink-0 self-center rounded-full bg-warning" />
        )}
        {children}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-row-id={rowId}
      className={rowClassName}
    >
      {hasFlag && (
        <span className="h-1.5 w-1.5 shrink-0 self-center rounded-full bg-warning" />
      )}
      {children}
    </button>
  )
}
